import { Box, Typography, Link, useTheme, Container } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { AuroraGlow } from "../../components/auth/AuroraGlow";
import { makeAuthColors } from "../../components/auth/authStyles";

const TermsPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { textPrimary, textSecondary } = makeAuthColors(isDark);
  // Body copy over the glass reads white in dark mode, not the theme's grey.
  const bodyColor = isDark ? "rgba(255, 255, 255, 0.92)" : "#374151";

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        overflow: "hidden",
        py: 6,
        px: 2,
      }}
    >
      <AuroraGlow />
      <Container maxWidth="md" sx={{ position: "relative" }}>
        {/* Logo */}
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Link component={RouterLink} to="/sign-in" sx={{ display: "inline-block" }}>
            <Box
              component="img"
              src={isDark ? "/insightLongLogoWhite.svg" : "/insightLongLogoPrimary.svg"}
              alt="Insight Analytics"
              sx={{ height: 32, width: "auto", display: "block" }}
            />
          </Link>
        </Box>

        {/* Header on the aurora */}
        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "2.25rem", sm: "2.5rem" },
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
            color: textPrimary,
            textAlign: "center",
          }}
        >
          Terms of Service
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            mb: 4,
            fontSize: "0.875rem",
            color: textSecondary,
            textAlign: "center",
          }}
        >
          Last Updated: 28 October 2026
        </Typography>

        {/* Glass content card */}
        <Box
          sx={{
            borderRadius: 4,
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(255, 255, 255, 0.5)",
            backdropFilter: "blur(12px)",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            p: { xs: 3, sm: 5 },
            mb: 4,
          }}
        >
          {/* Section 1 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            1. Acceptance of Terms
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            These Terms of Service ("Terms") constitute a legally binding agreement between you
            and Insight Investing LTD (company number 15628034, registered in England and Wales)
            ("Company", "we", "us", or "our") governing your access to and use of the Insight
            platform, including our website, web application, and all related services
            (collectively, the "Services"). By accessing or using the Services, you acknowledge
            that you have read, understood, and agree to be bound by these Terms and our Privacy
            Policy. If you do not agree with these Terms, you must not access or use the Services.
          </Typography>

          {/* Section 2 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            2. Modifications to Terms
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            We reserve the right to modify, amend, or update these Terms at any time at our sole
            discretion. We will provide notice of material changes by posting the updated Terms on
            our website with a revised "Last Updated" date, and where appropriate, by sending you
            an email notification to the address associated with your account. Your continued use
            of the Services following the posting of revised Terms constitutes your acceptance of
            such changes.
          </Typography>

          {/* Section 3 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            3. Description of Services
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            Insight is a subscription-based financial research and analysis platform that provides:
            Company Financial Analysis, AI-Powered Insights, Portfolio Management Tools, Earnings
            Information, Market Data, Insider Trading Data, and Competitive Analysis.
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            3.1 Informational Purposes Only
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>CRITICAL:</strong> The Services are provided STRICTLY for informational,
            educational, and research purposes only. The Services are NOT intended for use in
            making investment decisions. All data, analysis, insights, and content are provided to
            assist you in conducting your own independent research and should not form the basis of
            any investment decision.
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            3.2 Third-Party Data Sources
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            ALL financial data, market data, and company information provided through the Services
            are sourced from third-party data providers, primarily Financial Modeling Prep and
            other external market data sources. We are a data aggregation and presentation platform
            and do not independently verify, validate, or guarantee the accuracy of any data
            obtained from these third-party providers.
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            3.3 No Guarantee of Accuracy
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            WE DO NOT GUARANTEE THE ACCURACY, COMPLETENESS, RELIABILITY, CORRECTNESS, OR TIMELINESS
            OF ANY DATA, INFORMATION, ANALYSIS, OR CONTENT PROVIDED THROUGH THE SERVICES. Financial
            data may contain errors, inaccuracies, omissions, or significant delays. Market data
            may be delayed, incorrect, or subject to third-party licensing restrictions.
            AI-generated content may contain errors, biases, or incorrect conclusions. You
            acknowledge and agree that any reliance you place on data or content from the Services
            is strictly at your own risk.
          </Typography>

          {/* Section 4 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            4. Eligibility and Account Requirements
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            You must be at least 18 years of age to create an account or use the Services. When
            creating an account, you agree to provide accurate information, maintain current
            information, maintain account security, not share your account credentials, and
            immediately notify us of any suspected unauthorised access. You are solely responsible
            for all activities that occur under your account. You may not create multiple accounts,
            create an account on behalf of another person without permission, sell or transfer your
            account, or use automated means to create accounts.
          </Typography>

          {/* Section 5 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            5. Subscription Plans, Fees, and Billing
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            Access to the full features of the Services requires a paid subscription. By providing
            payment information and subscribing to a paid plan, you authorize us and our payment
            processors (including Lemon Squeezy) to charge the applicable subscription fees to your
            designated payment method. Subscriptions automatically renew at the end of each billing
            period unless cancelled prior to the renewal date. All fees are quoted in Pounds
            Sterling (GBP) unless otherwise stated. Prices may be subject to VAT depending on your
            location.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            App Store and Google Play Purchases. If you subscribe through our iOS or Android app,
            payment is charged to your Apple App Store or Google Play account and processed by
            Apple or Google rather than by us or our web payment processors. Prices are shown in
            the app before purchase, in your local currency, and may differ from the prices
            offered on our website. Subscriptions purchased through an app store renew
            automatically unless cancelled at least 24 hours before the end of the current billing
            period, and must be managed or cancelled through your App Store or Google Play
            subscription settings — they cannot be cancelled through our website or your account
            settings. Refunds for purchases made through an app store are handled by Apple or
            Google under their respective policies, and refund requests must be directed to the
            relevant store.
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            5.1 Refunds and Cancellations
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            All subscription fees are generally non-refundable. We do not provide refunds or
            credits for partial subscription periods, unused time, or general dissatisfaction with
            the Services. If you experience significant technical issues with the platform that
            prevent you from using the Services, you may be eligible for a refund. Contact our
            support team to request a refund for platform-related issues. You may cancel your
            subscription at any time through your account settings or by contacting our support
            team. Any refund provided for platform issues is your sole and exclusive remedy for
            technical problems with the Services. Refunds do not apply to investment losses, data
            accuracy issues, or third-party data provider errors.
          </Typography>

          {/* Section 6 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            6. Acceptable Use and Prohibited Conduct
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            Subject to your compliance with these Terms and payment of applicable fees, we grant
            you a limited, non-exclusive, non-transferable, non-sublicensable, revocable licence to
            access and use the Services for your personal or internal business purposes. You agree
            not to use the Services for illegal activities, market manipulation, unauthorised
            access, interference, data scraping, reverse engineering, circumvention of security
            measures, commercial exploitation, data redistribution, competing services, providing
            false information, transmitting malicious code, or infringing intellectual property
            rights. Violation of this section may result in immediate suspension or termination of
            your account without notice, refund, or liability to you.
          </Typography>

          {/* Section 7 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            7. Intellectual Property Rights
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            All content, software, technology, data compilations, designs, graphics, text,
            information, and materials available through the Services (excluding User Content) are
            the property of the Company or our licensors and are protected by copyright, trademark,
            patent, trade secret, and other intellectual property laws. You may not remove, alter,
            or obscure any copyright, trademark, or other proprietary rights notices from the
            Services or any content, copy or exploit any content for commercial purposes without
            our express written permission, or create derivative works based on the Services.
          </Typography>

          {/* Section 8 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            8. User Content and Data
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            You retain all ownership rights in your User Content. We do not claim ownership of any
            data or information that you provide or input into the Services. By submitting,
            uploading, or storing User Content through the Services, you grant us a limited,
            non-exclusive, worldwide, royalty-free licence to use, store, copy, process, transmit,
            and display your User Content solely to the extent necessary to provide, operate,
            maintain, and improve the Services. We will not share, sell, or disclose your User
            Content to third parties except as described in our Privacy Policy or as required by
            law.
          </Typography>

          {/* Section 9 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            9. Privacy and Data Protection
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            Your privacy is important to us. Our collection, use, storage, and protection of your
            personal data are governed by our Privacy Policy, which is incorporated into these
            Terms by reference. For users in the United Kingdom and European Economic Area, we
            comply with the UK General Data Protection Regulation (UK GDPR) and EU General Data
            Protection Regulation (GDPR).
          </Typography>

          {/* Section 10 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            10. AI-Generated Content and Analysis
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            The Services include AI-generated content and analysis powered by large language models
            and machine learning algorithms. AI-generated content is produced by automated systems
            and may contain errors, inaccuracies, biases, or outdated information. We do not
            guarantee the accuracy, completeness, reliability, or correctness of any AI-generated
            content. You are solely responsible for independently verifying any AI-generated content
            or analysis before acting on it.
          </Typography>

          {/* Section 11 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            11. Service Availability and Modifications
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            We strive to provide reliable and continuous access to the Services. However, we do not
            guarantee that the Services will be available at all times or that access will be
            uninterrupted, timely, secure, or error-free. We do not provide refunds, credits, or
            service extensions for Service downtime, except where required by applicable law. We
            reserve the right to modify, suspend, or discontinue any aspect of the Services at any
            time, temporarily or permanently, with or without notice.
          </Typography>

          {/* Section 12 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            12. No Financial or Investment Advice
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>IMPORTANT:</strong> All information, data, analysis, content, and tools
            provided through the Services are for informational and educational purposes only. The
            Company is not a registered financial advisor, investment advisor, stockbroker, or
            fiduciary under UK or EU law. Nothing on the Services constitutes investment advice,
            financial advice, trading advice, or recommendations. We do not recommend, endorse, or
            make representations regarding the suitability, profitability, or advisability of any
            particular investment, security, portfolio, transaction, or investment strategy. Past
            performance is not indicative of future results. You should consult with a qualified
            and independent financial advisor before making any investment decisions. The Company is
            not authorised or regulated by the Financial Conduct Authority (FCA).
          </Typography>

          {/* Section 13 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            13. Disclaimers and Warranties
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICES ARE PROVIDED "AS IS,"
            "AS AVAILABLE," AND "WITH ALL FAULTS" WITHOUT ANY WARRANTIES, REPRESENTATIONS, OR
            CONDITIONS OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. WE EXPRESSLY
            DISCLAIM ALL WARRANTIES AND CONDITIONS, INCLUDING BUT NOT LIMITED TO: any implied
            warranty of accuracy, reliability, merchantability, fitness for a particular purpose,
            non-infringement, security, or uninterrupted access. You acknowledge that you use the
            Services entirely at your own risk and discretion.
          </Typography>

          {/* Section 14 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            14. Limitation of Liability
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            14.1 ZERO LIABILITY FOR INVESTMENT DECISIONS
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT THE SERVICES ARE NOT INTENDED FOR MAKING
            INVESTMENT DECISIONS AND WE ACCEPT ABSOLUTELY NO LIABILITY WHATSOEVER FOR ANY INVESTMENT
            DECISIONS, TRADING ACTIVITIES, OR FINANCIAL LOSSES OF ANY KIND. WE SHALL HAVE ZERO
            LIABILITY for any losses, damages, costs, or expenses arising from or related to:
            Investment Decisions, Financial Losses, Third-Party Data Errors, AI-Generated Content,
            Data Inaccuracies, or Service Unavailability. YOU USE THE SERVICES ENTIRELY AT YOUR OWN
            RISK. WE ARE NOT RESPONSIBLE FOR ANY FINANCIAL CONSEQUENCES OF YOUR INVESTMENT
            DECISIONS.
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            14.2 Third-Party Data Provider Disclaimer
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            All financial data is sourced from third-party providers. WE DO NOT VERIFY, VALIDATE,
            OR GUARANTEE THE ACCURACY OF ANY THIRD-PARTY DATA. We act solely as an aggregator and
            presenter of third-party data and accept no liability for errors or omissions in
            third-party data, delays in data updates or transmission, interruptions in third-party
            data feeds, changes to third-party data after publication, or incompleteness or
            inadequacy of third-party data.
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            14.3 Platform Issues and Refunds
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            If you experience technical issues, bugs, or problems with the platform itself (as
            opposed to investment-related concerns), your remedy is cancellation and refund as set
            forth in Section 5.1. We are not liable for any other damages, losses, or costs related
            to platform issues.
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            14.4 Exclusion of All Consequential Damages
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE COMPANY, ITS
            OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, OR LICENSORS BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING
            BUT NOT LIMITED TO: loss of profits, revenue, income, business opportunity, data,
            goodwill, reputation, investment losses, trading losses, business interruption, or costs
            of procurement of substitute services. This exclusion applies regardless of the legal
            theory and whether or not we have been advised of the possibility of such damages.
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            14.5 Statutory Rights
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            Nothing in these Terms excludes or limits our liability for fraud or fraudulent
            misrepresentation, any liability that cannot be excluded or limited under UK law, or
            your statutory consumer rights under the Consumer Rights Act 2015 or other applicable
            consumer protection laws.
          </Typography>

          {/* Section 15 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            15. Indemnification
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            You agree to indemnify, defend, and hold harmless the Company, its affiliates, and
            their respective officers, directors, employees, agents, licensors, and suppliers from
            and against any and all claims, liabilities, damages, losses, costs, expenses, or fees
            arising from or relating to your use or misuse of the Services, your violation of these
            Terms, your violation of any applicable laws or third-party rights, your User Content,
            or your investment decisions or trading activities.
          </Typography>

          {/* Section 16 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            16. Termination
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            You may terminate your account at any time by using the account deletion function within
            the Services or contacting our customer support team. We reserve the right, in our sole
            discretion, to suspend or terminate your access to the Services and your account at any
            time, for any reason or no reason, with or without notice. Upon termination, your right
            to access and use the Services will immediately cease, we may delete your account and
            User Content, and you will remain liable for any outstanding fees or charges owed.
            Termination of your account does not entitle you to any refund of subscription fees,
            except as required by applicable law or as expressly provided in these Terms.
          </Typography>

          {/* Section 17 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            17. Governing Law and Jurisdiction
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            These Terms and any disputes arising out of or in connection with them or the Services
            shall be governed by and construed in accordance with the laws of England and Wales,
            without regard to conflict of law principles. You and the Company irrevocably agree that
            the courts of England and Wales shall have exclusive jurisdiction to settle any dispute
            or claim arising out of or in connection with these Terms or the Services. If you are a
            consumer resident in the UK or European Economic Area, nothing in this section affects
            your statutory rights as a consumer, including your right to bring proceedings in your
            local courts.
          </Typography>

          {/* Section 18 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            18. General Provisions
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            These Terms, together with our Privacy Policy, constitute the entire agreement between
            you and the Company regarding the Services. If any provision of these Terms is held to
            be invalid, illegal, or unenforceable, the remaining provisions will remain in full
            force and effect. Our failure to enforce any right or provision of these Terms shall not
            constitute a waiver of such right or provision. You may not assign your rights or
            obligations under these Terms without our prior written consent. We are not liable for
            any delay or failure to provide the Services due to causes beyond our reasonable
            control, including internet or telecommunications failures, third-party data provider
            outages, cloud hosting failures, DDoS attacks, or government actions.
          </Typography>

          {/* Section 19 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            19. Notifications and Email Communications
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>Alerts you ask for.</strong> When you turn on alerts for a company or topic, we
            send you those alerts in the app and by email, and as push notifications on our mobile
            app if you have allowed them. These are part of the Services you have requested. Alerts
            are based on third-party data, may be delayed or incomplete, and are provided for
            information only.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>Our newsletter.</strong> We send our newsletter by email to every account,
            unless you opt out. It brings product updates about new features and improvements to our
            own similar services and occasional offers on those services, such as discounts or
            upgrades. We tell you this when you sign up,
            and you can opt out at any time. If your account was created before this version of
            these Terms, we added it to the newsletter when this version took effect, unless you had
            already switched these emails off or unsubscribed from them.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>Your controls.</strong> Every newsletter and alert email contains a one-click
            unsubscribe link. Unsubscribing from any newsletter email, or switching off email for
            "Updates and offers" under Notifications in your account settings, stops all newsletter
            emails, product updates and offers alike. Alerts you have set up are separate: they have
            their own unsubscribe link and carry on if you leave the newsletter. You can change your
            email and push preferences at any time under Notifications in your account settings. If
            an email to you permanently fails to deliver, or you mark one as spam, we stop sending
            non-essential email to that address. We keep that address on our list even after you
            delete your account, so that we don't email an address that bounced or was marked as
            spam; your other email choices are deleted with your account, and if you sign up again
            you are a new account, as our Privacy Policy explains. We will still send essential
            account messages such as email verification, password resets, and billing or security
            notices.
          </Typography>

          {/* Contact Information */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            Contact Information
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            If you have any questions, concerns, or comments about these Terms or the Services,
            please contact us:
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 1, color: bodyColor, lineHeight: 1.7 }}
          >
            Email: support@insightanalytics.io
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: bodyColor, lineHeight: 1.7 }}
          >
            For other inquiries: ben@insightanalytics.io
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 1, color: bodyColor, fontWeight: 600 }}
          >
            Company Details:
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 0.5, color: bodyColor }}
          >
            Insight Investing LTD
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 0.5, color: bodyColor }}
          >
            Company Number: 15628034
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 0.5, color: bodyColor }}
          >
            Registered in England and Wales
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 4, color: bodyColor }}
          >
            Full registered office address: Available on our website footer or contact us for
            details
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: bodyColor, fontStyle: "italic" }}
          >
            By using the Services, you acknowledge that you have read, understood, and agree to be
            bound by these Terms of Service.
          </Typography>
        </Box>

        {/* Back Link */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Link
            component={RouterLink}
            to="/sign-in"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              fontWeight: 600,
              fontSize: "0.9375rem",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Back to Sign In
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsPage;
